/**
 * Snapshot Manager Service
 *
 * Captures and restores orchestration step state before/after AI actions are applied,
 * enabling single-click undo. Stores snapshots in sessionStorage (session-scoped,
 * single-level undo per orchestration).
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 7.3, 7.4, 7.6
 */

import api from '../../../shared/utils/api';

/**
 * Storage key pattern for orchestration snapshots.
 * Only the most recent snapshot per orchestration is retained.
 */
const SNAPSHOT_KEY_PREFIX = 'orch_ai_snapshot_';

/**
 * API endpoint paths for orchestration step update operations.
 */
const ENDPOINTS = {
  UPDATE_STEP_URL: '/api/orchestration/updateOrchestrationStepUrl',
  UPDATE_STEP_BODY: '/api/orchestration/updateOrchestrationStepRequestBody',
  UPDATE_STEP_HEADERS: '/api/orchestration/updateOrchestrationStepHeaders',
  UPDATE_STEP_METHOD: '/api/orchestration/updateOrchestrationStepMethod',
  UPDATE_STEP_AUTHORIZATION: '/api/orchestration/updateOrchestrationStepAuthorization',
  UPDATE_STEP_SCRIPT: '/api/orchestration/updateOrchestrationStepScript',
};

/**
 * Build the sessionStorage key for a given orchestration.
 * @param {number} orchestrationId
 * @returns {string}
 */
function buildKey(orchestrationId) {
  return `${SNAPSHOT_KEY_PREFIX}${orchestrationId}`;
}

/**
 * Deep copy a step's relevant fields into a StepSnapshot object.
 * @param {Object} step - A step from the orchestration step list
 * @returns {Object} StepSnapshot
 */
function extractStepSnapshot(step) {
  const apiRequest = step.api_request || {};

  // Extract headers — normalize to [{key, value}]
  const rawHeaders = apiRequest.headers || apiRequest.request_headers || step.orchestration_api_request_headers || [];
  const headers = (Array.isArray(rawHeaders) ? rawHeaders : []).map(h => ({
    key: h.key || h.name || '',
    value: h.value || '',
  }));

  // Extract body
  let body = '';
  const rawBody = apiRequest.body || apiRequest.request_body || step.orchestration_api_request_body;
  if (rawBody) {
    if (typeof rawBody === 'string') {
      body = rawBody;
    } else if (rawBody.value !== undefined) {
      body = rawBody.value !== null ? String(rawBody.value) : '';
    } else if (rawBody.raw !== undefined) {
      body = rawBody.raw !== null ? String(rawBody.raw) : '';
    } else if (rawBody.body !== undefined) {
      body = rawBody.body !== null ? String(rawBody.body) : '';
    }
  }

  // Extract bodyType
  const bodyType = (rawBody && typeof rawBody === 'object' ? rawBody.type : null) || 'raw';

  // Extract auth — normalize to {type, ...details}
  let auth = { type: 'inherit' };
  const rawAuth = step.authorization_type || apiRequest.authorization || {};
  if (rawAuth) {
    const authType = rawAuth.code || rawAuth.type || rawAuth.name || 'inherit';
    const authDetails = step.orchestration_api_request_authorization_details
      || rawAuth.allowedKeys || rawAuth.value || rawAuth.details || [];
    auth = {
      type: authType,
      details: (Array.isArray(authDetails) ? authDetails : []).map(d => ({
        key: d.authorization_type_key_name || d.key_name || d.key || '',
        value: d.authorization_type_key_value || d.value || '',
      })),
    };
  }

  // Extract scripts
  const scripts = step.orchestration_api_request_scripts || apiRequest.scripts || [];
  let preScript = '';
  let postScript = '';
  if (Array.isArray(scripts)) {
    for (const s of scripts) {
      const code = parseScriptSourceCode(s.source_code || s.sourceCode || '');
      if (s.execution_time === 'prerequest' || s.executionTime === 'prerequest') {
        preScript = code;
      } else if (s.execution_time === 'postresponse' || s.executionTime === 'postresponse') {
        postScript = code;
      }
    }
  }

  return {
    stepOrder: step.step_order,
    stepId: step.api_request_id || apiRequest.id || step.id,
    method: apiRequest.method_name || apiRequest.method || 'GET',
    url: apiRequest.raw_url || apiRequest.url || '',
    headers: JSON.parse(JSON.stringify(headers)),
    body,
    bodyType,
    auth: JSON.parse(JSON.stringify(auth)),
    preScript,
    postScript,
  };
}

/**
 * Parse script source_code field which may be JSON-wrapped ({code: "..."}) or raw text.
 * @param {string} sourceCode
 * @returns {string}
 */
function parseScriptSourceCode(sourceCode) {
  if (!sourceCode) return '';
  try {
    const parsed = JSON.parse(sourceCode);
    if (typeof parsed === 'object' && parsed.code !== undefined) {
      return parsed.code || '';
    }
    return sourceCode;
  } catch {
    return sourceCode;
  }
}

/**
 * Capture a deep copy of all orchestration steps' current state.
 * Stores the snapshot in sessionStorage under a key scoped to the orchestration ID.
 * Only the most recent snapshot per orchestration is retained.
 *
 * @param {number} orchestrationId - Orchestration ID
 * @param {Array} steps - Current orchestration steps
 * @returns {boolean} True if snapshot stored successfully, false if quota exceeded
 */
export function captureSnapshot(orchestrationId, steps) {
  if (!orchestrationId || !Array.isArray(steps)) {
    return false;
  }

  const snapshot = {
    orchestrationId,
    timestamp: new Date().toISOString(),
    steps: steps.map(extractStepSnapshot),
  };

  try {
    const key = buildKey(orchestrationId);
    sessionStorage.setItem(key, JSON.stringify(snapshot));
    return true;
  } catch (error) {
    // QuotaExceededError or other storage failure
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      return false;
    }
    // For any other unexpected error, also return false gracefully
    return false;
  }
}

/**
 * Check if a snapshot exists for the given orchestration.
 *
 * @param {number} orchestrationId
 * @returns {boolean}
 */
export function hasSnapshot(orchestrationId) {
  if (!orchestrationId) return false;
  const key = buildKey(orchestrationId);
  return sessionStorage.getItem(key) !== null;
}

/**
 * Retrieve the stored snapshot for an orchestration.
 *
 * @param {number} orchestrationId
 * @returns {Object|null} OrchestrationSnapshot or null if not found/invalid
 */
export function getSnapshot(orchestrationId) {
  if (!orchestrationId) return null;
  const key = buildKey(orchestrationId);
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;

  try {
    const snapshot = JSON.parse(raw);
    // Basic validation
    if (!snapshot || !Array.isArray(snapshot.steps)) {
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Remove the stored snapshot for an orchestration.
 *
 * @param {number} orchestrationId
 */
export function clearSnapshot(orchestrationId) {
  if (!orchestrationId) return;
  const key = buildKey(orchestrationId);
  sessionStorage.removeItem(key);
}

/**
 * Determine if a step's current state differs from its snapshot state.
 * Compares all captured fields: method, url, headers, body, bodyType, auth, preScript, postScript.
 *
 * @param {Object} snapshotStep - StepSnapshot from the stored snapshot
 * @param {Object} currentStep - Current step (raw orchestration step data)
 * @returns {boolean} True if the step has changed
 */
function hasStepChanged(snapshotStep, currentStep) {
  const current = extractStepSnapshot(currentStep);

  if (current.method !== snapshotStep.method) return true;
  if (current.url !== snapshotStep.url) return true;
  if (current.body !== snapshotStep.body) return true;
  if (current.bodyType !== snapshotStep.bodyType) return true;
  if (current.preScript !== snapshotStep.preScript) return true;
  if (current.postScript !== snapshotStep.postScript) return true;

  // Compare headers
  if (JSON.stringify(current.headers) !== JSON.stringify(snapshotStep.headers)) return true;

  // Compare auth
  if (JSON.stringify(current.auth) !== JSON.stringify(snapshotStep.auth)) return true;

  return false;
}

/**
 * Restore a single step to its snapshot state by calling the appropriate backend endpoints.
 *
 * @param {number} orchestrationId
 * @param {Object} snapshotStep - StepSnapshot to restore to
 * @returns {Promise<void>}
 */
async function restoreStep(orchestrationId, snapshotStep) {
  const { stepOrder, method, url, headers, body, bodyType, auth, preScript, postScript } = snapshotStep;

  // Update method
  await api.post(ENDPOINTS.UPDATE_STEP_METHOD, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    new_method: method,
  });

  // Update URL
  await api.post(ENDPOINTS.UPDATE_STEP_URL, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    new_url: url,
  });

  // Update headers
  await api.post(ENDPOINTS.UPDATE_STEP_HEADERS, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    new_headers: headers.map(h => ({ key: h.key || '', value: h.value || '' })),
  });

  // Update body
  let bodyPayload = {};
  if (body) {
    try {
      bodyPayload = JSON.parse(body);
    } catch {
      bodyPayload = { raw: body };
    }
  }
  await api.post(ENDPOINTS.UPDATE_STEP_BODY, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    new_body: bodyPayload,
  });

  // Update authorization
  await api.post(ENDPOINTS.UPDATE_STEP_AUTHORIZATION, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    authorization_type: auth.type || 'inherit',
    authorization_details: (auth.details || []).map(d => ({
      key: d.key || '',
      value: d.value || '',
    })),
  });

  // Update scripts (pre-request and post-response together)
  await api.post(ENDPOINTS.UPDATE_STEP_SCRIPT, {
    orchestration_id: orchestrationId,
    step_order: stepOrder,
    scripts: [
      {
        execution_time: 'prerequest',
        type: 'text/javascript',
        source_code: JSON.stringify({ code: preScript || '' }),
      },
      {
        execution_time: 'postresponse',
        type: 'text/javascript',
        source_code: JSON.stringify({ code: postScript || '' }),
      },
    ],
  });
}

/**
 * Restore orchestration to snapshot state by calling backend update endpoints.
 * Only updates steps that differ from their current state.
 * Retains the snapshot on partial failure so the user can retry.
 *
 * @param {number} orchestrationId
 * @param {Object} snapshot - OrchestrationSnapshot (from getSnapshot)
 * @param {Array} currentSteps - Current orchestration steps for diff comparison
 * @returns {Promise<{reverted: number, failed: Array}>}
 */
export async function restoreSnapshot(orchestrationId, snapshot, currentSteps) {
  const result = { reverted: 0, failed: [] };

  if (!snapshot || !Array.isArray(snapshot.steps) || !Array.isArray(currentSteps)) {
    return result;
  }

  // Build a map of current steps by step_order for diff comparison
  const currentByOrder = {};
  for (const step of currentSteps) {
    currentByOrder[step.step_order] = step;
  }

  // Identify steps that have changed and need restoration
  const stepsToRestore = [];
  for (const snapshotStep of snapshot.steps) {
    const currentStep = currentByOrder[snapshotStep.stepOrder];
    if (!currentStep) {
      // Step was deleted — can't restore it (report as failed)
      result.failed.push({
        stepOrder: snapshotStep.stepOrder,
        error: 'Step no longer exists in orchestration',
      });
      continue;
    }
    if (hasStepChanged(snapshotStep, currentStep)) {
      stepsToRestore.push(snapshotStep);
    }
  }

  // Restore each changed step
  for (const snapshotStep of stepsToRestore) {
    try {
      await restoreStep(orchestrationId, snapshotStep);
      result.reverted++;
    } catch (error) {
      result.failed.push({
        stepOrder: snapshotStep.stepOrder,
        error: error.message || 'Failed to restore step',
      });
    }
  }

  // Only clear snapshot on full success (no failures)
  // On partial failure, retain snapshot for retry (Requirement 7.6)
  if (result.failed.length === 0) {
    clearSnapshot(orchestrationId);
  }

  return result;
}

export { SNAPSHOT_KEY_PREFIX, extractStepSnapshot, hasStepChanged, buildKey };
